import { Box, Checkbox, Typography } from '@mui/material'

import { AppointmentType, GroupedAppointmentTypes } from './utils'

type AppointmentTypeMappingSummaryProps = {
  groupedAppointmentTypes: GroupedAppointmentTypes[]
  mappedAppointmentTypeKeys: Set<string>
  onToggleMapping: (appointmentType: AppointmentType, nextMapped: boolean) => void
  disabled?: boolean
  disabledReason?: string
}

const AppointmentTypeMappingSummary = ({
  groupedAppointmentTypes,
  mappedAppointmentTypeKeys,
  onToggleMapping,
  disabled,
  disabledReason,
}: AppointmentTypeMappingSummaryProps) => {
  if (!groupedAppointmentTypes.length) return null

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: '1.4rem', fontWeight: '500', mb: disabledReason ? 0.5 : 1 }}>
        Appointment Types
      </Typography>
      {disabled && disabledReason && (
        <Typography
          sx={{ color: 'text.secondary', fontSize: '1.2rem', mb: 1, fontStyle: 'italic' }}
        >
          {disabledReason}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {groupedAppointmentTypes.map(({ group, appointmentTypes: groupApptTypes }) => {
          return (
            <Box
              key={group?.id ?? 'ungrouped'}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                minWidth: 180,
              }}
            >
              {group && (
                <Typography
                  sx={{ color: 'text.secondary', fontSize: '1.1rem', fontWeight: '500', mb: 0.5 }}
                >
                  {group.title}
                </Typography>
              )}
              {groupApptTypes.map(apptType => {
                const isMapped = mappedAppointmentTypeKeys.has(apptType.unique_key)

                return (
                  <Box key={apptType.id} sx={{ display: 'flex', alignItems: 'center', ml: -0.5 }}>
                    <Checkbox
                      checked={isMapped}
                      disabled={disabled}
                      size="small"
                      color="secondary"
                      sx={{ py: 0.25 }}
                      onChange={() => {
                        if (disabled) return
                        onToggleMapping(apptType, !isMapped)
                      }}
                      slotProps={{
                        input: {
                          'aria-label': `${apptType.name} mapped`,
                        },
                      }}
                    />
                    <Typography sx={{ fontSize: '1.3rem' }}>{apptType.name}</Typography>
                  </Box>
                )
              })}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default AppointmentTypeMappingSummary
